import {memo} from 'react';
import type {ReactNode} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import SectionHeader from '@/components/Common/SectionHeader';
import {borderRadius, colors, fontSize, fontWeight, lineHeight, shadows, spacing} from '@/styles/tokens';
import {useInView} from '@/hooks/useInView';

export interface HorizontalStep {
    /** Icon element. Attached on the page so data files stay JSX-free. */
    icon?: ReactNode;
    title: string;
    description: string;
}

export interface HorizontalStepFlowProps {
    eyebrow?: string;
    heading: string;
    subheading?: string;
    /** Steps in order. The number badge is derived from position — never passed in. */
    steps: HorizontalStep[];
}

const ICON_CARD = 64;

// One continuous sweep left to right: the line travels between icons and each icon pops
// as the sweep reaches it. The row is built from half-width segments — each step draws
// the half to its left and the half to its right — so a gap between two icons is two
// segments, and `SEGMENT_MS` is the time to cross one of them.
//
// Keyframes rather than transitions: an animation runs off a single class change, and
// `fill-mode: both` holds each piece at its start state through its delay, which is what
// makes the sweep read as one motion instead of everything moving at once.
const SEGMENT_MS = 180;

// Time from one icon to the next: the two segments between them.
const GAP_MS = SEGMENT_MS * 2;

const ICON_POP_MS = 380;

const rowSx = {
    display: 'flex',
    flexDirection: {xs: 'column', md: 'row'},
    alignItems: 'flex-start',
    gap: {xs: 5, md: 0}
} as const;

const stepSx = {
    flex: {md: '1 1 0'},
    width: '100%',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    textAlign: 'center' as const
} as const;

// The connector is built from two half-width pseudo-elements per step rather than
// one absolutely-positioned line: each step draws the segment to its left and to
// its right, and the end caps are simply omitted. That keeps the line correct at
// any step count and any container width, with no per-index rules to maintain
// (which is what makes the connectors in the legacy `Process` component brittle).
const segmentBaseSx = {
    display: {xs: 'none', md: 'block'},
    position: 'absolute',
    top: ICON_CARD / 2,
    height: '1px',
    // A step darker than the card borders around it: at 1px on a white ground the border
    // token read as almost nothing, and the line is what carries the sequence.
    bgcolor: colors.neutral.grayMedium,
    zIndex: 0
} as const;

const iconHolderSx = {
    position: 'relative' as const,
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    '&::before, &::after': segmentBaseSx
} as const;

// Which halves this step draws. `first-of-type` cannot do this job: the icon holder
// is an only child of its <li>, so it is always both first and last of its type —
// the position has to come from the step index instead.
const segmentBefore = {content: '""', left: 0, right: '50%'} as const;
const segmentAfter = {content: '""', left: '50%', right: 0} as const;

// `linear`, not an ease: consecutive segments butt up against each other, so any
// per-segment easing would make the sweep visibly stutter at each halfway point.
const sweepSx = (delay: number) =>
    ({
        transformOrigin: 'left center',
        animation: `hsfLineIn ${SEGMENT_MS}ms linear ${delay}ms both`,
        '@media (prefers-reduced-motion: reduce)': {animation: 'none'}
    } as const);

// Sweep order across the whole row is: icon 0, step 0's right half, step 1's left half,
// icon 1, … — so a step's left half runs one segment *before* its own icon, and its
// right half one segment after.
const connectorSx = (isFirst: boolean, isLast: boolean, animating: boolean, index: number) =>
    ({
        ...iconHolderSx,
        '&::before': isFirst
            ? {content: 'none'}
            : {...segmentBaseSx, ...segmentBefore, ...(animating && sweepSx(index * GAP_MS - SEGMENT_MS))},
        '&::after': isLast ? {content: 'none'} : {...segmentBaseSx, ...segmentAfter, ...(animating && sweepSx(index * GAP_MS))}
    } as const);

// The icon pops when the sweep arrives at it. The number badge is a child, so it scales
// with the card rather than needing its own animation.
const iconPopSx = (index: number) =>
    ({
        ...iconCardSx,
        animation: `hsfIconPop ${ICON_POP_MS}ms ease-out ${index * GAP_MS}ms both`,
        '@media (prefers-reduced-motion: reduce)': {animation: 'none'}
    } as const);

const iconCardSx = {
    position: 'relative' as const,
    zIndex: 1,
    width: ICON_CARD,
    height: ICON_CARD,
    borderRadius: borderRadius.xl,
    bgcolor: colors.background.default,
    boxShadow: shadows.lg,
    color: colors.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {fontSize: fontSize[28]}
} as const;

const badgeSx = {
    position: 'absolute' as const,
    top: -10,
    right: -10,
    zIndex: 2,
    width: 24,
    height: 24,
    borderRadius: borderRadius.circle,
    bgcolor: colors.primary.main,
    color: colors.text.inverse,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: fontSize[12],
    fontWeight: fontWeight.bold,
    lineHeight: 1
} as const;

const titleSx = {
    mt: spacing[20],
    fontSize: fontSize[15],
    fontWeight: fontWeight.bold,
    color: colors.text.primary,
    lineHeight: lineHeight[24]
} as const;

const descriptionSx = {
    mt: spacing[8],
    px: spacing[12],
    fontSize: fontSize[13],
    fontWeight: fontWeight.regular,
    color: colors.text.primary,
    lineHeight: lineHeight[20]
} as const;

/**
 * HorizontalStepFlow — a compact left-to-right run of numbered stages: icon card,
 * step number, short title and one line of copy, joined by a connector line.
 *
 * Rendered as an ordered list, so the sequence survives without the visual line —
 * a screen reader announces "1 of 5". Below `md` the steps stack and the connector
 * disappears.
 *
 * The first time the row scrolls into view, the connector sweeps left to right and each
 * icon pops as the sweep reaches it. The animation is additive: without it every stage is
 * simply visible, so a failed observer or a reader who asked for less motion still gets
 * the whole flow.
 */
const HorizontalStepFlow = ({eyebrow, heading, subheading, steps}: HorizontalStepFlowProps) => {
    const [setRowRef, inView] = useInView({triggerOnce: true, threshold: 0.25});

    return (
        <MUIContainer>
            <SectionHeader eyebrow={eyebrow} heading={heading} subheading={subheading} />

            <Box component="ol" ref={setRowRef} sx={{...rowSx, listStyle: 'none', m: 0, p: 0}}>
                {steps.map((step, index) => (
                    <Box component="li" key={step.title} sx={stepSx}>
                        <Box sx={connectorSx(index === 0, index === steps.length - 1, inView, index)}>
                            <Box sx={inView ? iconPopSx(index) : iconCardSx}>
                                {step.icon}
                                <Box component="span" aria-hidden className="font-headers" sx={badgeSx}>
                                    {index + 1}
                                </Box>
                            </Box>
                        </Box>
                        <Typography component="h3" className="font-headers" sx={titleSx}>
                            {step.title}
                        </Typography>
                        <Typography component="p" className="font-regular-text" sx={descriptionSx}>
                            {step.description}
                        </Typography>
                    </Box>
                ))}
            </Box>

            <style>{`
                @keyframes hsfLineIn {
                    from { transform: scaleX(0); }
                    to   { transform: scaleX(1); }
                }
                @keyframes hsfIconPop {
                    0%   { opacity: 0; transform: scale(0.4); }
                    60%  { opacity: 1; transform: scale(1.12); }
                    100% { opacity: 1; transform: scale(1); }
                }
            `}</style>
        </MUIContainer>
    );
};

export default memo(HorizontalStepFlow);
